import { formatTime } from '../utils/dateUtils'
import { isSessionHappeningNow, formatTimeRemaining, isSessionEnded } from '../utils/timeUtils'
import { SpeakerImage } from './SpeakerImage'
import { SessionCalendarButton } from './SessionCalendarButton'
import { PrerequisitesWarning } from './PrerequisitesWarning'

export function SessionCard({ 
  session, 
  isStarred, 
  isExpanded, 
  isInConflict = false,
  onToggleStar, 
  onToggleExpand, 
  onExportComplete,
  onSpeakerClick,
  currentTime = new Date()
}) {
  const handleSpeakerClick = (speaker, e) => {
    e.stopPropagation()
    onSpeakerClick?.(speaker)
  }

  const handleStarClick = (e) => {
    e.stopPropagation()
    onToggleStar(session.id)
  }

  const handleCardClick = () => {
    onToggleExpand(session.id)
  }

  const getCategoryStyle = (category) => {
    const baseStyle = "inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide mb-2"
    
    switch (category.toLowerCase()) {
      case 'dev mode':
        return `${baseStyle} bg-gradient-to-r from-editions-green to-emerald-400 text-white`
      case 'biz mode':
        return `${baseStyle} bg-gradient-to-r from-editions-purple to-editions-purple-light text-white`
      case 'general':
        return `${baseStyle} bg-gradient-to-r from-gray-600 to-gray-500 text-white`
      case 'mainstage':
        return `${baseStyle} bg-gradient-to-r from-editions-orange to-yellow-400 text-black`
      default:
        return `${baseStyle} bg-gradient-to-r from-gray-600 to-gray-500 text-white`
    }
  }

  const isLive = isSessionHappeningNow(session, currentTime)
  const isEnded = isSessionEnded(session, currentTime)

  return (
    <div 
      className={`
        bg-white/5 backdrop-blur-sm border rounded-xl p-4 cursor-pointer
        transition-all duration-300 ease-out transform hover:scale-[1.01]
        ${isExpanded 
          ? 'border-editions-purple/50 shadow-lg shadow-editions-purple/20 bg-white/10' 
          : 'border-white/20 hover:border-editions-purple/30 hover:shadow-md hover:shadow-editions-purple/10'
        }
        ${isInConflict ? 'ring-2 ring-red-500/50' : ''}
        ${isEnded ? 'opacity-60 grayscale' : ''}
      `}
      onClick={handleCardClick}
      data-session-id={session.id}
    >
      {/* Conflict Warning Banner */}
      {isInConflict && (
        <div className="mb-3 -mx-4 -mt-4 px-4 py-2 bg-red-500/20 border-b border-red-400/30 rounded-t-xl">
          <div className="flex items-center gap-2 text-red-300 text-sm">
            <span>⚠️</span>
            <span className="font-medium">Time Conflict</span>
            <span className="text-xs text-red-400">This session overlaps with another in your plan</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0 max-w-[calc(100%-120px)]">
          <div className="mb-2">
            <h3 className={`font-semibold text-base leading-tight ${
              isLive ? 'live-session-animated' : 'text-white'
            }`}>
              {session.title}
            </h3>
            {isLive && (
              <div className="flex items-center gap-1 mt-1" title="Live now">
                <div className="w-2 h-2 bg-editions-green rounded-full animate-pulse"></div>
                <span className="text-xs text-editions-green font-medium">LIVE</span>
              </div>
            )}
            {isEnded && !isLive && (
              <div className="flex items-center gap-1 mt-1" title="Session has ended">
                <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                <span className="text-xs text-gray-500 font-medium">ENDED</span>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-300">
            <span className="flex items-center gap-1 flex-shrink-0">
              🕒 {formatTime(session.startTime)} - {formatTime(session.endTime)}
              {isLive && (
                <span className="ml-2 text-editions-orange font-medium">
                  ({formatTimeRemaining(session, currentTime)})
                </span>
              )}
            </span>
            <span className="flex items-center gap-1 flex-shrink-0">
              📍 {session.location}
            </span>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0 w-[110px] justify-end">
          {/* Calendar Export Button */}
          <SessionCalendarButton
            session={session}
            isExpanded={isExpanded}
            onExportComplete={onExportComplete}
          />
          
          {/* Star Button */}
          <button
            onClick={handleStarClick}
            title={isStarred ? "Remove from My Plan" : "Add to My Plan"}
            className={`
              p-2 rounded-lg transition-colors duration-200 flex-shrink-0
              ${isStarred 
                ? 'text-editions-orange bg-editions-orange/20 hover:bg-editions-orange/30' 
                : 'text-gray-400 hover:text-editions-orange hover:bg-editions-orange/10'
              }
            `}
            aria-label={isStarred ? "Remove from My Plan" : "Add to My Plan"}
          >
            <svg 
              className="w-5 h-5" 
              fill={isStarred ? 'currentColor' : 'none'} 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" 
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Category and Content Type Tags */}
      <div className="flex flex-wrap gap-2 mb-3">
        <span className={`text-xs px-3 py-1 rounded-full font-medium ${
          session.category === 'Dev mode' 
            ? 'bg-editions-green/20 text-editions-green'
            : session.category === 'Biz mode'
            ? 'bg-editions-purple/20 text-editions-purple-light'
            : 'bg-gray-600/20 text-gray-300'
        }`}>
          {session.category}
        </span>
        {session.contentType && (
          <span className="text-xs px-3 py-1 rounded-full bg-white/10 text-gray-300 font-medium">
            {session.contentType}
          </span>
        )}
      </div>

      {/* Speakers - Show in collapsed state */}
      {!isExpanded && session.speakers && session.speakers.length > 0 && (
        <div className="mb-3">
          <div className="text-xs text-gray-400 mb-2">Speakers:</div>
          <div className="flex items-center gap-2 flex-wrap">
            {session.speakers.slice(0, 3).map((speaker, index) => (
              <div key={index} className="flex items-center gap-2">
                <SpeakerImage speaker={speaker} size="xs" />
                <span className="text-sm text-gray-300">{speaker.name}</span>
              </div>
            ))}
            {session.speakers.length > 3 && (
              <span className="text-xs text-gray-400 ml-1">
                +{session.speakers.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Description (when expanded) */}
      {isExpanded && session.description && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <p className="text-gray-300 leading-relaxed whitespace-pre-line">
            {session.description}
          </p>
        </div>
      )}

      {/* Prerequisites Warning (when expanded and has prerequisites) */}
      {isExpanded && session.prerequisites && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <PrerequisitesWarning session={session} />
        </div>
      )}

      {/* Speakers with Images (when expanded) */}
      {isExpanded && session.speakers && session.speakers.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <h4 className="text-white font-medium mb-3">Speakers</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {session.speakers.map((speaker, index) => (
              <button
                key={index}
                onClick={(e) => handleSpeakerClick(speaker, e)}
                title={`View ${speaker.name}'s profile and sessions`}
                className="flex items-center gap-3 text-left p-3 rounded-lg 
                         bg-white/5 hover:bg-white/10 transition-all duration-200
                         transform hover:scale-[1.02] border border-white/10 hover:border-editions-purple/30"
              >
                <SpeakerImage speaker={speaker} size="md" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-white text-sm truncate">
                    {speaker.name}
                  </div>
                  {speaker.title && (
                    <div className="text-xs text-gray-400 truncate">
                      {speaker.title}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Expand/Collapse Indicator */}
      <div className="flex justify-center mt-3 pt-3 border-t border-white/10">
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <span>{isExpanded ? 'Click to collapse' : 'Click to expand'}</span>
          <svg 
            className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    </div>
  )
} 